import {groupBy} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState, useEffect} from 'react';

import {convertStrandToDisplayName, Strand} from 'constants/MolbioConstants';
import {
  doesRegionOverlapGroup,
  getTranslationNameOrPlaceholder,
  TranslationRegionGroupData,
} from 'molbio/translations/TranslationUtil';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import {GRAY_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';

import {sharedFormStyles, TranslationRegion} from './CreateEditTranslationForm';
import {translationPositionToString} from './TranslationPanelUtil';

const DEFAULT_STRAND_SECTION_ORDER = [1, -1, 0];

interface TranslationsDropdownProps {
  selectedTranslation: TranslationRegionGroupData | null;
  onChangeSelectedTranslation: (translation: TranslationRegionGroupData) => void;
  allTranslationData: Array<TranslationRegionGroupData>;
  sequenceStartIndex: number;
  sequenceLength: number;
  selectedStrand: Strand;
  region: TranslationRegion;
}

/**
 * A dropdown component of translations in a sequence
 */
function TranslationsDropdown({
  selectedTranslation,
  onChangeSelectedTranslation,
  allTranslationData,
  sequenceStartIndex,
  sequenceLength,
  selectedStrand,
  region,
}: TranslationsDropdownProps): JSX.Element {
  const [allTranslationDataByStrand, setAllTranslationDataByStrand] = useState<{
    [strand: number]: Array<TranslationRegionGroupData>;
  }>({});
  const [sectionOrder, setSectionOrder] = useState<Array<number>>(DEFAULT_STRAND_SECTION_ORDER);

  useEffect(() => {
    setAllTranslationDataByStrand(groupBy(allTranslationData, (t) => t.strand));
  }, [allTranslationData]);

  useEffect(() => {
    // Show the matching strand section of enabled translation options first
    setSectionOrder([
      selectedStrand,
      ...DEFAULT_STRAND_SECTION_ORDER.filter((strand) => strand !== selectedStrand),
    ]);
  }, [selectedStrand]);

  return (
    <FormGroup className={css(sharedFormStyles.fieldRow)}>
      <FieldLabel label="Add as region to" />
      <SelectDropdown
        className={css(sharedFormStyles.fieldInput)}
        displayName={
          selectedTranslation == null
            ? 'Select translation'
            : getTranslationNameOrPlaceholder(selectedTranslation, sequenceStartIndex, sequenceLength)
        }
        alwaysDropDown={true}
        menu={
          <Menu value={selectedTranslation?.cid} width={MenuWidth.FULL} scrollable={true}>
            {sectionOrder.map((strand) => (
              <Section key={strand}>
                <SectionHeader>{convertStrandToDisplayName(strand as Strand)}</SectionHeader>
                {(allTranslationDataByStrand[strand] ?? []).map((group) => (
                  <Option
                    onSelect={() => {
                      onChangeSelectedTranslation(group);
                    }}
                    value={group.cid}
                    key={group.cid}
                    isDisabled={
                      group.strand !== selectedStrand ||
                      doesRegionOverlapGroup(group, region.start, region.end)
                    }
                    tooltipTitle={
                      doesRegionOverlapGroup(group, region.start, region.end)
                        ? 'Translation regions cannot overlap'
                        : null
                    }
                  >
                    {getTranslationNameOrPlaceholder(group, sequenceStartIndex, sequenceLength)}
                    <p className={css(styles.optionSubtext)}>
                      {translationPositionToString(group, sequenceStartIndex, sequenceLength)}
                    </p>
                  </Option>
                ))}
              </Section>
            ))}
          </Menu>
        }
      />
    </FormGroup>
  );
}

const styles = StyleSheet.create({
  optionSubtext: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARK,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: '0',
  },
});

export default TranslationsDropdown;
